import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  useWindowDimensions,
  Modal,
  TextInput,
  Pressable,
} from "react-native";
import { useNavigation, useRoute, RouteProp, useFocusEffect } from "@react-navigation/native";
import { formatPct } from '../../utils/formatPercentage';
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import Svg, { Path, Circle } from "react-native-svg";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  XIcon,
  ExportIcon,
} from "../../components/Icons";
import { COLORS, FONTS } from "../../constants/config";
import { PageContainer, Navbar, Breadcrumb } from "../../components";
import { GoalsSkeleton } from "../../components/Skeleton";
import PendingDeletionBadge from "../../components/PendingDeletionBadge";
import DeletionImpactModal from "../../components/DeletionImpactModal";
import {
  useResponsive,
  useRoleNavigation,
  useAutoTranslate,
} from "../../hooks";
import { MainStackParamList } from "../../navigation/MainNavigator";
import { processesApi, subProcessesApi, goalsApi, kpisApi } from "../../services/apiService";
import { useProcessesQuery } from "../../hooks/queries/useProcessesQuery";
import { OrganizationFilterDropdown, useCanViewAllOrgs } from "../../components/OrganizationFilterDropdown";
import { queryKeys } from "../../providers/QueryProvider";
import { exportProcessesToExcel } from "../../utils/excelExport";
import { ProcessList, StrategicGoalList } from "../../types";
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { useYear } from "../../contexts/YearContext";

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================
const FileInputIcon = ({ size = 13, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const HomeIcon = ({
  size = 16,
  color = "#6B7280",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const MoreVertIcon = ({
  size = 16,
  color = "#6B7280",
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="1" fill={color} />
    <Circle cx="12" cy="12" r="1" fill={color} />
    <Circle cx="12" cy="19" r="1" fill={color} />
  </Svg>
);

// Donut Chart Component
const DonutChart = ({
  percentage,
  size = 60,
  strokeWidth = 6,
  textColor = COLORS.neutral900,
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  textColor?: string;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Color based on achievement percentage
  // >= 100%: Green (ممتاز), >= 80%: Orange (جيد), < 80%: Red (يحتاج تحسين)
  let color = "#EF4444"; // Red - Needs Improvement (< 80%)
  if (percentage >= 100)
    color = "#22C55E"; // Green - Excellent
  else if (percentage >= 80) color = "#F97316"; // Orange - Good

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View
        style={{
          position: "absolute",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            fontSize: 16,
            fontFamily: FONTS.numbers,
            fontWeight: "500",
            color: textColor,
          }}
        >
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

type RouteProps = RouteProp<MainStackParamList, 'Processes'>;

export default function ProcessesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const organizationName = route.params?.organizationName;
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding } =
    useResponsive();
  const { showError, showSuccess } = useNotification();
  const { user } = useAuth();
  // DTS 2.2 "اقتراح العناصر التشغيلية" (processes are one of the 5 types):
  //   Section Head = إنشاء وقراءة وتعديل ; everyone else = اطلاع or —.
  //   The coarse !isViewOnly from useRoleNavigation lets Directors and
  //   Employees see Add/Edit when data entry is open — gate on the
  //   permission so only Section Head + superuser get the affordance.
  // DTS 2.2 "الحذف المنطقي" lives on hrcpms.soft_delete (Strategy Team
  //   + superuser/tech-admin only).
  const canCreateProcess = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.executive_items.create')
  );
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Themed styles for dark mode support
  const themedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageTitle: { color: colors.gray900 },
    pageSubtitle: { color: colors.textMuted },
  };

  const [refreshing, setRefreshing] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<number | null>(null);
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [kpiCounts, setKpiCounts] = useState<Record<number, number>>({});
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  // Responsive - using the hook
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;

  const queryClient = useQueryClient();

  // Main data query — React Query shows cached data instantly on focus,
  // refetches silently in the background (stale-while-revalidate).
  const {
    data: processesData,
    isLoading: loading,
    refetch,
  } = useProcessesQuery({
    page: currentPage,
    pageSize: itemsPerPage,
    department_id: effectiveOrgId,
    strategic_goal: selectedGoal ?? undefined,
    search: searchQuery.trim() || undefined,
    year: selectedYear,
  });

  const processes: ProcessList[] = useMemo(
    () => processesData?.results || [],
    [processesData],
  );
  const totalCount = processesData?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Goals dropdown — separate query, rarely changes.
  const { data: goalsDropdownData } = useQuery({
    queryKey: ['goals', 'dropdown', effectiveOrgId ?? null],
    queryFn: async () => {
      const goalsParams: Record<string, any> = { page_size: 100 };
      if (effectiveOrgId) goalsParams.department_id = effectiveOrgId;
      return goalsApi.list(goalsParams);
    },
    staleTime: 10 * 60 * 1000,
  });
  const goals: StrategicGoalList[] = goalsDropdownData?.results || [];

  // After processes load, populate the per-process KPI counts. This is still
  // a fan-out of 1 request per process, but it runs in the background after
  // the main list has already rendered — no spinner on focus.
  useEffect(() => {
    if (!processes.length) {
      setKpiCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const counts = await Promise.all(
        processes.map(async (p) => {
          try {
            const kpis = await kpisApi.list({ source_process: p.id, page_size: 1 });
            return { id: p.id, count: kpis.count || 0 };
          } catch {
            return { id: p.id, count: 0 };
          }
        }),
      );
      if (cancelled) return;
      const countsMap: Record<number, number> = {};
      counts.forEach((c) => {
        countsMap[c.id] = c.count;
      });
      setKpiCounts(countsMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [processes]);

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  // Back-compat shim for handlers that still call fetchData() — invalidates
  // the React Query cache so the next access triggers a refetch.
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
  }, [queryClient]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
    setShowGoalDropdown(false);
  };

  const handleClearFilter = () => {
    setSelectedGoal(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      showError(t('common.noDataToExport'));
      return;
    }
    setIsExporting(true);
    try {
      const params: Record<string, any> = { page_size: 9999 };
      if (effectiveOrgId) params.department_id = effectiveOrgId;
      if (selectedGoal) params.strategic_goal = selectedGoal;
      if (searchQuery) params.search = searchQuery;
      if (selectedYear) params.year = selectedYear;

      const allData = await processesApi.list(params);
      const allProcesses = allData?.results || [];

      if (allProcesses.length === 0) {
        showError(t('common.noDataToExport'));
        return;
      }

      // Build process ID → name mapping and fetch KPIs
      const processNameMap: Record<number, string> = {};
      allProcesses.forEach((p: any) => { processNameMap[p.id] = p.name; });

      const processIds = allProcesses.map((p: any) => p.id);
      const kpiPromises = processIds.map((id: number) =>
        kpisApi.list({ source_process: id, page_size: 9999 })
      );
      const kpiResults = await Promise.all(kpiPromises);
      const allKpis = kpiResults.flatMap((res: any, idx: number) =>
        (res?.results || []).map((kpi: any) => ({
          ...kpi,
          process_name: processNameMap[processIds[idx]] || '',
        }))
      );

      // Fetch sub-processes for all processes
      const subProcessParams: Record<string, any> = { page_size: 9999 };
      if (effectiveOrgId) subProcessParams.department_id = effectiveOrgId;
      if (selectedYear) subProcessParams.year = selectedYear;
      const subProcessData = await subProcessesApi.list(subProcessParams);
      const allSubProcesses = (subProcessData?.results || []).filter(
        (sp: any) => processIds.includes(sp.process)
      );

      // Fetch KPIs for sub-processes
      const subProcessNameMap: Record<number, string> = {};
      allSubProcesses.forEach((sp: any) => { subProcessNameMap[sp.id] = sp.name; });

      const subProcessIds = allSubProcesses.map((sp: any) => sp.id);
      const spKpiPromises = subProcessIds.map((id: number) =>
        kpisApi.list({ source_sub_process: id, page_size: 9999 })
      );
      const spKpiResults = await Promise.all(spKpiPromises);
      const allSubProcessKpis = spKpiResults.flatMap((res: any, idx: number) =>
        (res?.results || []).map((kpi: any) => ({
          ...kpi,
          sub_process_name: subProcessNameMap[subProcessIds[idx]] || '',
        }))
      );

      exportProcessesToExcel(allProcesses, allKpis, allSubProcesses, allSubProcessKpis);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddProcess = () => {
    navigation.navigate("AddProcess" as any);
  };

  const handleViewDetails = (id: number) => {
    navigation.navigate("ProcessDetail", { id });
  };

  const toggleMenu = (id: number) => {
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  const handleEdit = (id: number) => {
    setActiveMenuId(null);
    navigation.navigate("AddProcess" as any, { processId: id });
  };

  const handleDeleteClick = (id: number) => {
    setActiveMenuId(null);
    setDeleteConfirmId(id);
    setShowDeletionImpact(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    try {
      const response = await processesApi.delete(deleteConfirmId);

      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t("processes.deleteSuccess"));
      }

      fetchData();
    } catch (err: any) {
      if (err.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        fetchData();
      } else if (err.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(err.response?.data?.detail || t("processes.deleteFailed"));
      }
    } finally {
      setDeleting(false);
      setShowDeletionImpact(false);
      setDeleteConfirmId(null);
    }
  };

  // Calculate achievement percentage from API data
  const getAchievementPercentage = (process: ProcessList) => {
    // Use achievement_percentage from API if available
    if (process.achievement_percentage !== undefined) {
      return parseFloat(Number(process.achievement_percentage).toFixed(2));
    }
    // Fallback: calculate based on weight or return 0
    const weight = parseFloat(process.weight || "0");
    return weight > 0 ? Math.min(Math.round(weight), 100) : 0;
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render — matches
  // GoalsScreen / InitiativesScreen pattern.
  if (loading && processes.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[COLORS.primary]}
          />
        }
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={organizationId ? [
              { label: t("navigation.home") },
              { label: t("nav.organizations"), route: "Organizations" },
              { label: organizationName || "", route: "OrganizationDetail", params: { id: organizationId } },
              { label: t("processes.title") },
            ] : [
              { label: t("navigation.home") },
              { label: t("processes.title") },
            ]}
          />

          {/* Header Card - matches Figma ArTitle section */}
          <View
            style={[
              styles.headerCard,
              themedCardStyles.card,
              { zIndex: 1000 },
            ]}
          >
            {/* Row 1: Title and Add Button */}
            <View
              style={[styles.headerRow, isSmallScreen && styles.headerRowSmall]}
            >
              <Text style={[styles.pageTitle, themedCardStyles.pageTitle]}>
                {t("processes.title")}
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                {/* Add button — Section Head + superuser only per DTS 2.2 */}
                {canCreateProcess && (
                  <TouchableOpacity
                    style={styles.addButton}
                    onPress={handleAddProcess}
                  >
                    <Text style={styles.addButtonText}>
                      {t("processes.addMainProcess")}
                    </Text>
                    <PlusIcon size={14} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
                {/* Import (bulk-create) — same gate as Add */}
                {canCreateProcess && (
                  <TouchableOpacity
                    style={[styles.exportButton, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}
                    onPress={() => navigation.navigate('ImportProcesses' as any)}
                  >
                    <Text style={[styles.exportButtonText, { color: colors.textPrimary }]}>{t('importProcesses.title')}</Text>
                    <FileInputIcon size={13} color={colors.textPrimary} />
                  </TouchableOpacity>
                )}
                {/* Export button */}
                <TouchableOpacity
                  style={[styles.exportButton, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}
                  onPress={handleExportExcel}
                  disabled={isExporting}
                >
                  <Text style={[styles.exportButtonText, { color: colors.textPrimary }]}>
                    {t('common.exportExcel')}
                  </Text>
                  <ExportIcon size={13} color={colors.textPrimary} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Row 2: Filter */}
            <View style={styles.filterRow}>
              {/* Clear Filter Button - Only shown when filter is active */}
              {(selectedGoal || searchQuery) && (
                <TouchableOpacity
                  style={styles.clearFilterButton}
                  onPress={handleClearFilter}
                >
                  <XIcon size={14} color="#EF4444" />
                  <Text style={styles.clearFilterText}>
                    {t("filters.clearFilter")}
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={[
                  styles.applyFilterButton,
                  { backgroundColor: isDark ? "#374151" : "#F1F5F9" },
                ]}
                onPress={handleApplyFilter}
              >
                <Text
                  style={[
                    styles.applyFilterText,
                    { color: isDark ? "#F9FAFB" : "#374151" },
                  ]}
                >
                  {t("common.applyFilter")}
                </Text>
              </TouchableOpacity>

              <View style={styles.dropdownContainer}>
                <TextInput
                  style={[
                    styles.searchInput,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.cardBorder,
                      color: colors.text,
                    },
                  ]}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder={t("processes.searchPlaceholder")}
                  placeholderTextColor={colors.textSecondary}
                  textAlign="right"
                />
              </View>

              <View style={styles.dropdownContainer}>
                <TouchableOpacity
                  style={[
                    styles.dropdown,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.cardBorder,
                    },
                  ]}
                  onPress={() => setShowGoalDropdown(!showGoalDropdown)}
                >
                  <ChevronDownIcon size={16} color={colors.textSecondary} />
                  <Text style={[styles.dropdownText, { color: colors.text }]}>
                    {selectedGoal
                      ? translate(
                          goals.find((g) => g.id === selectedGoal)?.title,
                        ) || t("kpis.allGoals")
                      : t("kpis.allGoals")}
                  </Text>
                </TouchableOpacity>

                {showGoalDropdown && (
                  <>
                  <Pressable style={styles.dropdownOverlay} onPress={() => setShowGoalDropdown(false)} />
                  <View
                    style={[
                      styles.dropdownMenu,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.cardBorder,
                      },
                    ]}
                  >
                    <ScrollView nestedScrollEnabled style={{ maxHeight: 250 }}>
                      <TouchableOpacity
                        style={[
                          styles.dropdownItem,
                          { borderBottomColor: colors.cardBorder },
                        ]}
                        onPress={() => {
                          setSelectedGoal(null);
                          setShowGoalDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            { color: colors.text },
                          ]}
                        >
                          {t("kpis.allGoals")}
                        </Text>
                      </TouchableOpacity>
                      {goals.map((goal) => (
                        <TouchableOpacity
                          key={goal.id}
                          style={[
                            styles.dropdownItem,
                            { borderBottomColor: colors.cardBorder },
                          ]}
                          onPress={() => {
                            setSelectedGoal(goal.id);
                            setShowGoalDropdown(false);
                          }}
                        >
                          <Text
                            style={[
                              styles.dropdownItemText,
                              { color: colors.text },
                            ]}
                          >
                            {translate(goal.title)}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                  </>
                )}
              </View>

              {/* Organization filter - rightmost in RTL = first reading position */}
              {canViewAllOrgs && (
                <View style={styles.dropdownContainer}>
                  <OrganizationFilterDropdown
                    selectedOrgId={selectedOrgId}
                    onChange={setSelectedOrgId}
                  />
                </View>
              )}
            </View>
          </View>

          {/* Process Cards Grid */}
          <View
            style={[
              styles.cardsGrid,
              isSmallScreen && styles.cardsGridSmall,
              { zIndex: 1 },
            ]}
          >
            {processes.map((process) => {
              const percentage = getAchievementPercentage(process);
              const isPending = process.approval_status === 'pending';
              return (
                <View
                  key={process.id}
                  style={[
                    styles.card,
                    isSmallScreen && styles.cardSmall,
                    themedCardStyles.card,
                    activeMenuId === process.id && { zIndex: 1000, position: 'relative' },
                  ]}
                >
                  <View style={styles.cardHeader}>
                    <View style={styles.cardTitleSection}>
                      <Text
                        style={[styles.cardTitle, themedCardStyles.cardTitle]}
                      >
                        {translate(process.name)}
                      </Text>
                      {isPending && (
                        <View style={styles.pendingBadge}>
                          <Text style={styles.pendingBadgeText}>{t('processes.pendingApproval')}</Text>
                        </View>
                      )}
                      {process.has_pending_deletion && <PendingDeletionBadge compact />}
                      {(process.organizational_units_details || []).map(u => (
                        <View key={u.id} style={styles.badge}>
                          <Text style={styles.badgeText}>
                            {translate(u.name_ar || u.name)}
                          </Text>
                        </View>
                      ))}
                      <Text
                        style={[
                          styles.cardDescription,
                          themedCardStyles.cardDescription,
                        ]}
                        numberOfLines={2}
                      >
                        {translate(process.description) ||
                          t("common.noDescription")}
                      </Text>
                    </View>
                    <DonutChart
                      percentage={percentage}
                      size={80}
                      strokeWidth={7}
                      textColor={colors.textPrimary}
                    />
                  </View>

                  {/* Stats Row */}
                  <View style={styles.statsRow}>
                    <View
                      style={[
                        styles.statBox,
                        { backgroundColor: isDark ? "#374151" : "#F1F5F9" },
                      ]}
                    >
                      <Text
                        style={[
                          styles.statValue,
                          { color: isDark ? "#F9FAFB" : "#171717" },
                        ]}
                      >
                        {process.sub_processes_count || 0}
                      </Text>
                      <Text
                        style={[
                          styles.statLabel,
                          { color: isDark ? "#9CA3AF" : "#6B7280" },
                        ]}
                      >
                        {t("processes.subProcessesCount")}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.statBox,
                        { backgroundColor: isDark ? "#374151" : "#F1F5F9" },
                      ]}
                    >
                      <Text
                        style={[
                          styles.statValue,
                          { color: isDark ? "#F9FAFB" : "#171717" },
                        ]}
                      >
                        {kpiCounts[process.id] || 0}
                      </Text>
                      <Text
                        style={[
                          styles.statLabel,
                          { color: isDark ? "#9CA3AF" : "#6B7280" },
                        ]}
                      >
                        {t("processes.kpisCount")}
                      </Text>
                    </View>
                    {/* Relative weight hidden per user request */}
                    {/* <View style={[styles.statBox, { backgroundColor: isDark ? '#374151' : '#F1F5F9' }]}>
                      <Text style={[styles.statValue, { color: isDark ? '#F9FAFB' : '#171717' }]}>{parseFloat(process.weight || '0').toFixed(2)} %</Text>
                      <Text style={[styles.statLabel, { color: isDark ? '#9CA3AF' : '#6B7280' }]}>{t('common.relativeWeight')}</Text>
                    </View> */}
                  </View>

                  {/* Card Footer */}
                  <View
                    style={[
                      styles.cardFooter,
                      { borderTopColor: colors.cardBorder },
                      (!canCreateProcess && !canSoftDelete) && { justifyContent: 'flex-end' },
                    ]}
                  >
                    {/* Card menu — visible when there's at least one
                        action the user is allowed to perform. */}
                    {(canCreateProcess || canSoftDelete) && (
                      <View style={styles.menuContainer}>
                        <TouchableOpacity
                          style={styles.moreButton}
                          onPress={() => toggleMenu(process.id)}
                        >
                          <MoreVertIcon size={20} color={colors.textSecondary} />
                        </TouchableOpacity>
                        {activeMenuId === process.id && (
                        <View
                          style={[
                            styles.menuDropdown,
                            {
                              backgroundColor: colors.card,
                              borderColor: colors.cardBorder,
                            },
                          ]}
                        >
                          {canCreateProcess && (
                            <TouchableOpacity
                              style={[
                                styles.menuItem,
                                { borderBottomColor: colors.cardBorder },
                              ]}
                              onPress={() => handleEdit(process.id)}
                            >
                              <Text
                                style={[
                                  styles.menuItemText,
                                  { color: colors.text },
                                ]}
                              >
                                {t("common.edit")}
                              </Text>
                              <EditIcon size={16} color={colors.textSecondary} />
                            </TouchableOpacity>
                          )}
                          {canSoftDelete && (
                            <TouchableOpacity
                              style={[styles.menuItem, styles.menuItemDanger]}
                              onPress={() => handleDeleteClick(process.id)}
                            >
                              <Text style={styles.menuItemTextDanger}>
                                {t("common.delete")}
                              </Text>
                              <TrashIcon size={16} color="#EF4444" />
                            </TouchableOpacity>
                          )}
                        </View>
                        )}
                      </View>
                    )}
                    <TouchableOpacity
                      style={styles.detailsLink}
                      onPress={() => handleViewDetails(process.id)}
                    >
                      <Text
                        style={[
                          styles.detailsLinkText,
                          { color: colors.textSecondary },
                        ]}
                      >
                        {t("common.viewDetails")}
                      </Text>
                      <ChevronLeftIcon size={14} color={colors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Empty State */}
          {processes.length === 0 && (
            <View style={styles.emptyState}>
              <Text
                style={[styles.emptyStateText, { color: colors.textMuted }]}
              >
                {t("processes.noProcesses")}
              </Text>
              {/* Empty-state Add — same DTS gate as the toolbar button */}
              {canCreateProcess && (
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={handleAddProcess}
                >
                  <PlusIcon size={16} color="#FFFFFF" />
                  <Text style={styles.addButtonText}>
                    {t("processes.addNewProcess")}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <View style={[styles.pagination, { flexDirection: rtlRow }]}>
              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  currentPage === 1 && styles.paginationButtonDisabled,
                ]}
                onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <Text
                  style={[
                    styles.paginationButtonText,
                    { color: colors.text },
                    currentPage === 1 && { color: colors.textMuted },
                  ]}
                >
                  {t("common.previous")}
                </Text>
                <ChevronRightIcon
                  size={14}
                  color={currentPage === 1 ? colors.textMuted : colors.text}
                />
              </TouchableOpacity>

              <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
                {(() => {
                  const maxVisible = 5;
                  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  const end = Math.min(totalPages, start + maxVisible - 1);
                  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                })().map((page) => (
                  <TouchableOpacity
                    key={page}
                    style={[
                      styles.paginationNumber,
                      { backgroundColor: isDark ? "#374151" : "#F3F4F6" },
                      currentPage === page && styles.paginationNumberActive,
                    ]}
                    onPress={() => setCurrentPage(page)}
                  >
                    <Text
                      style={[
                        styles.paginationNumberText,
                        { color: isDark ? "#F9FAFB" : "#374151" },
                        currentPage === page &&
                          styles.paginationNumberTextActive,
                      ]}
                    >
                      {page}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  currentPage === totalPages && styles.paginationButtonDisabled,
                ]}
                onPress={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
              >
                <ChevronLeftIcon
                  size={14}
                  color={currentPage === totalPages ? colors.textMuted : colors.text}
                />
                <Text
                  style={[
                    styles.paginationButtonText,
                    { color: colors.text },
                    currentPage === totalPages && { color: colors.textMuted },
                  ]}
                >
                  {t("common.next")}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setDeleteConfirmId(null); }}
        onConfirm={handleConfirmDelete}
        entityName={processes.find(p => p.id === deleteConfirmId)?.name || ''}
        fetchImpact={async () => {
          const response = await processesApi.deletionImpact(deleteConfirmId!);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={deleting}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 24,
    paddingTop: 16,
  },
  containerSmall: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#6B7280",
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  // Header Card
  headerCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 24,
    gap: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  headerRow: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
  },
  orgFilterRow: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginTop: 12,
    zIndex: 1100,
  },
  headerRowSmall: {
    flexDirection: "column-reverse",
    alignItems: "flex-end",
    gap: 12,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    color: "#414042",
    textAlign: "right",
  },
  exportButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    minHeight: 44,
  },
  exportButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#414042",
    lineHeight: 20,
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
    minHeight: 44,
  },
  addButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#f1f5f9",
  },

  // Filter Row
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 20,
    zIndex: 1000,
  },
  dropdownContainer: {
    position: "relative",
    zIndex: 9999,
  },
  dropdownOverlay: {
    position: "fixed" as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9998,
  },
  searchInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minWidth: 200,
    minHeight: 44,
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  dropdown: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minWidth: 200,
    minHeight: 44,
    gap: 8,
  },
  dropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    flex: 1,
    textAlign: "right",
  },
  dropdownMenu: {
    position: "absolute",
    top: "100%",
    right: 0,
    left: 0,
    minWidth: 200,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    marginTop: 4,
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
    elevation: 50,
    zIndex: 9999,
    overflow: "hidden",
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
    minHeight: 44,
    justifyContent: "center",
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    textAlign: "right",
  },
  applyFilterButton: {
    backgroundColor: "#F1F5F9",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    minHeight: 44,
    justifyContent: "center",
  },
  applyFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#0F172A",
  },
  clearFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FCA5A5",
    backgroundColor: "#FEF2F2",
    minHeight: 44,
    gap: 6,
  },
  clearFilterText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#EF4444",
  },

  // Cards Grid
  cardsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: 16,
  },
  cardsGridSmall: {
    gap: 12,
  },
  card: {
    width: "48.5%",
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "visible",
  },
  cardSmall: {
    width: "100%",
    padding: 16,
  },
  cardHeader: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
  },
  cardTitleSection: {
    flex: 1,
    marginLeft: 16,
    alignItems: "flex-end",
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: "right",
    marginBottom: 8,
  },
  badge: {
    backgroundColor: "#a6813f",
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    marginBottom: 8,
  },
  badgeText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: "#FFFFFF",
  },
  pendingBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    marginBottom: 8,
  },
  pendingBadgeText: {
    fontSize: 12,
    fontFamily: FONTS.medium,
    color: "#FFFFFF",
  },
  cardDescription: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
    textAlign: "right",
    lineHeight: 20,
  },

  // Stats Row
  statsRow: {
    flexDirection: "row-reverse",
    alignItems: "stretch",
    gap: 8,
    marginBottom: 16,
  },
  statBox: {
    flex: 1,
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  statValue: {
    fontSize: 16,
    fontFamily: FONTS.numbers,
    fontWeight: "500",
    color: COLORS.neutral900,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: COLORS.neutral500,
    textAlign: "center",
  },

  // Card Footer
  cardFooter: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  detailsLink: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: 4,
  },
  detailsLinkText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: COLORS.neutral600,
  },
  moreButton: {
    padding: 4,
  },
  menuContainer: {
    position: "relative",
    zIndex: 100,
  },
  menuDropdown: {
    position: "absolute",
    bottom: "100%",
    right: 0,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    minWidth: 120,
    marginBottom: 4,
    zIndex: 1000,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  menuItemDanger: {
    borderBottomWidth: 0,
  },
  menuItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#374151",
  },
  menuItemTextDanger: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#EF4444",
  },

  // Empty State
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: COLORS.neutral500,
    marginBottom: 16,
  },

  // Pagination
  pagination: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 24,
    gap: 8,
  },
  paginationButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 4,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#374151",
  },
  paginationButtonTextDisabled: {
    color: "#D1D5DB",
  },
  paginationNumbers: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  paginationNumber: {
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F3F4F6",
  },
  paginationNumberActive: {
    backgroundColor: COLORS.primary,
  },
  paginationNumberText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#374151",
  },
  paginationNumberTextActive: {
    color: "#FFFFFF",
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "90%",
    maxWidth: 400,
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: "#374151",
    marginBottom: 12,
    textAlign: "center",
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 24,
  },
  modalButtons: {
    flexDirection: "row",
    gap: 12,
  },
  modalButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 100,
    minHeight: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  modalButtonCancel: {
    backgroundColor: "#F3F4F6",
  },
  modalButtonDelete: {
    backgroundColor: "#EF4444",
  },
  modalButtonCancelText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#374151",
  },
  modalButtonDeleteText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: "#FFFFFF",
  },
});
